import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { observer } from "mobx-react";
import { Component, type SyntheticEvent } from "react";
import { type NotificationEmailCreate, type Setting, type SettingUpdate } from "../../api";
import { type FormUpdateProps } from "../../utils/form-update-props";
import { createUuid, type UUID } from "../../utils/uuid";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { DefaultButton, Stack } from "@fluentui/react";
import { ElofleetDialogFooter } from "../atoms/elofleet-dialog-footer";
import sizes from "../sizes.scss";
import { NotificationEmails } from "../atoms/notification-emails";

export interface FormUpdateSettingVehicleProps extends Omit<FormUpdateProps<Setting>, "id"> {}

import { store } from "../../store";

@observer
export class FormUpdateSettingVehicle extends Component<FormUpdateSettingVehicleProps> {
    private validationId = createUuid();

    @observable private accessor settingId: UUID | undefined;
    @observable private accessor emailsCreate: NotificationEmailCreate[] = [];

    constructor(props: FormUpdateSettingVehicleProps) {
        super(props);
        makeObservable(this);
    }

    async componentDidMount(): Promise<void> {
        // We can always assume that the API will return exactly one setting object
        const [setting] = await store.repositorySettings.byQueryAsync();
        runInAction(() => {
            this.settingId = setting.id;
        });
        store.repositorySettings.validation.update.initializeModel(
            this.validationId,
            this.settingUpdate,
            setting.id,
        );
    }

    @action.bound private onChangeEmails(): void {
        this.triggerValidation();
    }

    private triggerValidation(): void {
        store.repositorySettings.validation.update.updateModel(
            this.validationId,
            this.settingUpdate,
        );
    }

    public componentWillUnmount(): void {
        if (this.setting === undefined) {
            return;
        }
        store.repositorySettings.discardMutableCopy(FormUpdateSettingVehicle.name, this.setting.id);
    }

    @computed private get setting(): Setting | undefined {
        if (this.settingId === undefined) {
            return undefined;
        }
        return store.repositorySettings.mutableCopyById(
            FormUpdateSettingVehicle.name,
            this.settingId,
        );
    }

    @computed private get settingUpdate(): SettingUpdate {
        return {
            maintenanceNotificationEmails: this.setting?.maintenanceNotificationEmails,
            maintenanceNotificationEmailsCreate:
                this.emailsCreate.length == 0 ? null : this.emailsCreate,
        };
    }

    @action.bound
    private async updateSetting(evt: SyntheticEvent<HTMLFormElement>): Promise<void> {
        if (this.setting === undefined) {
            throw new Error("submit button should be disabled until setting is loaded");
        }
        evt.preventDefault();
        const setting = await store.repositorySettings.update(this.setting.id, this.settingUpdate);
        this.props.onUpdate(setting);
    }

    public render(): JSX.Element {
        const primaryButton = (
            <PrimaryButtonValidation
                text={store.i18n.t("formUpdateSettingVehicle.submit.text")}
                validation={store.repositorySettings.validation.update}
                validationId={this.validationId}
            />
        );

        if (this.setting != undefined) {
            return (
                <form onSubmit={this.updateSetting}>
                    <Stack
                        tokens={{
                            childrenGap: `${sizes.gutter}`,
                            padding: `0px ${sizes.xl}`,
                        }}
                        styles={{
                            root: {
                                maxHeight: sizes.halfHeight,
                                overflow: "auto",
                            },
                        }}
                    >
                        <NotificationEmails
                            emailsCreate={this.emailsCreate}
                            emailsUpdate={this.setting.maintenanceNotificationEmails}
                            onChange={this.onChangeEmails}
                            translationPrefixKey={"formUpdateSettingVehicle"}
                            validationId={this.validationId}
                            validationKey={"maintenanceNotificationEmails"}
                            validationKeyCreate={"maintenanceNotificationEmailsCreate"}
                        ></NotificationEmails>
                    </Stack>
                    {this.props.asDialogContent ? (
                        <ElofleetDialogFooter>
                            <DefaultButton
                                text={store.i18n.t("formUpdateSettingVehicle.cancel.text")}
                                onClick={this.props.onDialogCancel}
                            />
                            {primaryButton}
                        </ElofleetDialogFooter>
                    ) : (
                        <Stack horizontal horizontalAlign="end">
                            {primaryButton}
                        </Stack>
                    )}
                </form>
            );
        } else return <></>;
    }
}
